import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { debounce } from "lodash";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

import { Accession, Case, LISCase, LISCaseList } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { useHasLoaded } from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import selectLabClinics from "shared/selectors/selectLabClinics";

import { createAccession } from "app/actions/accessionActions";
import { getModMedCase, listCases } from "app/actions/caseActions";
import { scanSpecimen } from "app/actions/specimenActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReactComponent as LogoSVG } from "assets/logo.svg";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import { defaultAccession } from "stations/AccessioningStationEdit";
import AccessionNumber from "ui/components/AccessionNumber";
import AppSpinner from "ui/components/AppSpinner";
import CasePriority from "ui/components/CasePriority";
import ClinicName from "ui/components/ClinicName";
import ErrorToastBody from "ui/components/ErrorToastBody";
import SpecimenProtocolTags from "ui/components/SpecimenProtocolTags";
import UserFullName from "ui/components/UserFullName";
import {
  ModMedPWPayload,
  accessionFromPayload,
  mmBarcodeFromSpecimen,
  modMedFromState,
} from "util/objectFromBarcode";
import { specimenSort } from "util/sortFunctions";
import { workProcessState } from "util/workProcessUtils";

const CaseAccession = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const casesState = useAppSelector((state) => state.casesWithWork);
  const sortedCaseIds = useAppSelector((state) => state.sys.caseListSortedIds);
  const cases = useMemo(() => {
    const results = Object.values(casesState);
    if (sortedCaseIds)
      results.sort(
        (a, b) =>
          sortedCaseIds.indexOf(a.id || 0) - sortedCaseIds.indexOf(b.id || 0)
      );
    return results;
  }, [casesState, sortedCaseIds]);
  const lab = useSelectedLab();
  const station = useSelectedLabStation();
  const [pageCount, setPageCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const casesReady = useHasLoaded(listCases, {
    parentLookupLabStationId: station.id,
  });

  const clinics = useAppSelector((state) => selectLabClinics(state));

  const users = useAppSelector((sys) => sys.users);

  const columnHelper = createColumnHelper<LISCaseList>();

  // TODO: we should have backend guarantees that collected_date is available
  // ... so this is "just in case" but could be removed
  // I am not sure about this actually. Denormalizing on the backend is far less efficient than doing so on the frontend
  // and we should have everything we need here already. That being said, I am not actually sure what Case.collected_date
  // is supposed to be denormalizing. - Tyler
  const case_collected_date = (row: LISCaseList) => {
    return row.collected_date
      ? row.collected_date
      : row.specimens?.at(0)?.collected_date;
  };

  const onScanModMedSpecimen = useCallback(
    (mod_med_payload: ModMedPWPayload) => {
      dispatch(
        getModMedCase({
          parentLookupLabStationId: station.id,
          externalOrderId: mod_med_payload.external_order_id,
          medicalRecordNumber: mod_med_payload.medical_record_number,
          externalPatientId: mod_med_payload.external_patient_id,
          firstName: mod_med_payload.first_name,
          lastName: mod_med_payload.last_name,
        })
      )
        .then((response: LISCase) => {
          navigate(`/cases/${response.id}`);

          if (mod_med_payload.specimen_name) {
            const specimen = response.specimens.find(
              (s) => s.name === mod_med_payload?.specimen_name
            );

            if (specimen?.id) {
              dispatch(
                scanSpecimen({
                  id: specimen.id,
                  parentLookupLabStationId: station.id,
                })
              );
            } else {
              toast.error(
                `Specimen ${mod_med_payload.specimen_name} was not found in Case ${response.accession}`
              );
            }
          }
        })
        .catch((error) => {
          if (error.status === 404 && error.body?.code === "TooManyResults") {
            toast.error(
              `Could not uniquely identify ${mod_med_payload.external_order_id}`
            );
          } else if (error.status === 404) {
            dispatch(
              createAccession({
                parentLookupLabStationId: station.id,
                requestBody: defaultAccession(lab, station, {
                  ...accessionFromPayload(mod_med_payload),
                  received_date: DateTime.now().toISO(),
                }),
              })
            )
              .then((accession: Accession) => {
                navigate(
                  `/station/accessioning/${accession.id}?integration=modmed`
                );
              })
              // TODO: this is a lot of c&p boilerplate...
              // How can we make this a pattern that stays out of individual components?
              .catch((e: ApiError) => {
                e.message &&
                  toast.error(
                    (t) => (
                      <ErrorToastBody
                        e={e}
                        handleClose={() => toast.dismiss(t.id)}
                      />
                    ),
                    {
                      position: "bottom-right",
                      className: "Toast",
                    }
                  );
              });
          }
        });
    },
    [cases, station, lab]
  );

  const onScanSpecimen = useCallback(
    (specimen_id: number) => {
      dispatch(
        scanSpecimen({
          id: specimen_id,
          parentLookupLabStationId: station.id,
        })
      ).then((response) => {
        navigate(`/cases/${response?.id}`);
      });
    },
    [cases, station]
  );

  useBarcodeScanner((barcode) => {
    if (barcode.object_type === "specimen" && barcode.mod_med_payload) {
      onScanModMedSpecimen(barcode.mod_med_payload);
    } else if (barcode.object_type === "specimen") {
      onScanSpecimen(barcode.object_id);
    }
  });

  const { printSpecimen } = useBarcodePrintingCallbacks();

  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  const columns = [
    // NOTE: this was pretty ugly in my opinion. Leaving it here for posterity.
    // ... Displayed inline next to accession instead
    // columnHelper.accessor((row) => row.priority, {
    //   id: "priority",
    //   header: "",
    //   enableColumnFilter: true,
    //   filterFn: (row, _id, value) => {
    //     return row.original.priority === value;
    //   },
    //   cell: ({ row }) => <CasePriority priorityCase={row.original} />,
    // }),
    columnHelper.accessor(
      (row) => `${row.accession ?? ""} ${row.external_order_id ?? ""}`,
      {
        id: "accession",
        header: "Status & Accession",
        enableColumnFilter: true,
        filterFn: (row, _id, value) => {
          if (value) {
            return row.original.status === value;
          }
          return true;
        },
        cell: ({ row }) => {
          const accessioningWorkProcess = row.original.work_processes.find(
            (w) => !w.order && w.tasks.find((w) => w.type === "grossing")
          );
          return (
            <>
              {row.original.status === Case.status.WORKING ? (
                <>
                  <div className="Tag Tag--green">accessioned</div>
                  {accessioningWorkProcess && (
                    <div className="Tag Tag--purple">
                      {workProcessState(accessioningWorkProcess)}
                    </div>
                  )}
                </>
              ) : (
                <div className="Tag Tag--blue">{row.original.status}</div>
              )}

              <div className="Cases__row__accession">
                <CasePriority priorityCase={row.original} />

                {row.original.accession && (
                  <>
                    <AccessionNumber accession={row.original.accession} />
                  </>
                )}
              </div>
            </>
          );
        },
      }
    ),

    columnHelper.accessor(
      (row) =>
        `${row.patient?.last_name}, ${row.patient?.first_name} ${
          row.patient?.date_of_birth ?? ""
        } ${row.accession ?? ""} ${row.external_order_id ?? ""}`,
      {
        cell: ({ row }) => {
          const { patient } = row.original;
          return (
            <>
              <strong>
                {patient?.last_name}, {patient?.first_name}
              </strong>
              <br />
              {patient?.date_of_birth && (
                <span>
                  {DateTime.fromFormat(
                    patient?.date_of_birth,
                    "yyyy-MM-dd"
                  ).toLocaleString()}
                </span>
              )}
            </>
          );
        },
        id: "patient__last_name,patient__first_name",
        header: "Patient",
      }
    ),

    columnHelper.accessor(
      (row) => {
        const collected_date = case_collected_date(row);
        return (
          collected_date && DateTime.fromISO(collected_date).toLocaleString()
        );
      },
      {
        id: "specimens__collected_date",
        header: "Collected",
        enableColumnFilter: true,
        filterFn: (row, _id, value) => {
          return !!case_collected_date(row.original)?.includes(value);
        },
        cell: ({ getValue, row }) => {
          const collected_date = case_collected_date(row.original);
          const diffDate = collected_date
            ? DateTime.fromISO(collected_date).toRelative()
            : "";
          return (
            <div>
              {getValue()}
              <br />
              <span>{diffDate}</span>
            </div>
          );
        },
      }
    ),

    columnHelper.accessor(
      (row) => {
        const ordering_doctor = users[row.ordering_doctor_id || 0];
        return `${clinics[row.patient?.clinic_id || 0]?.name} ${
          ordering_doctor?.first_name
        } ${ordering_doctor?.last_name}`;
      },
      {
        cell: ({ row }) => {
          return (
            <>
              <strong>
                <ClinicName id={row.original.patient?.clinic_id} />
              </strong>
              <br />
              <span>
                <UserFullName id={row.original.ordering_doctor_id} />
              </span>
            </>
          );
        },
        header: "Provider",
        id: "patient__clinic__name",
        enableColumnFilter: true,
        filterFn: "includesString",
      }
    ),

    columnHelper.accessor(
      (row) => row.specimens?.map((s) => `${s.name} ${s.site}`).join(" "),
      {
        header: "Specimens",
        id: "specimens",
        enableSorting: false,
        cell: ({ row }) => {
          const { specimens } = row.original;
          return specimens && specimens.length > 0 ? (
            <>
              {[...specimens].sort(specimenSort).map((s) => (
                <div
                  key={s.id}
                  className="Scannable"
                  onClick={(e) => {
                    if (e.altKey && lisAltClickScanning) {
                      row.original.external_order_id
                        ? onScanModMedSpecimen(modMedFromState(row.original, s))
                        : s.id && onScanSpecimen(s.id);
                    }
                  }}
                >
                  <strong>{s.name}</strong> &mdash; {s.site}{" "}
                  {s.lis_state && (
                    <div className="Tag Tag--green">{s.lis_state}</div>
                  )}
                  <SpecimenProtocolTags specimen={s} specimens={specimens} />
                  {/* TODO: refactor out logic of which lis state to which colour */}
                </div>
              ))}
            </>
          ) : (
            <></>
          );
        },
      }
    ),

    columnHelper.display({
      id: "actions",
      cell: ({ row }) => (
        <Link
          to={`/cases/${row.original.id}`}
          className="Scannable"
          onClick={(e) => {
            if (e.altKey && lisAltClickScanning) {
              e.preventDefault();

              if (row.original.external_order_id) {
                window.open(
                  `/print_object/modmed/${row.original.specimens
                    ?.map((s) =>
                      encodeURIComponent(mmBarcodeFromSpecimen(row.original, s))
                    )
                    .join(",")}`
                );
              } else {
                printSpecimen(
                  row.original.specimens.map((s) => s.id) as number[],
                  row.original.id as number
                );
              }
            }
          }}
        >
          View &rarr;
        </Link>
      ),
    }),
  ];

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [clinicFilter, setClinicFilter] = useState<number | undefined>();
  const [collectedAtFilter, setCollectedAtFilter] = useState<
    string | undefined
  >();
  const [globalFilter, _setGlobalFilter] = useState("");
  const setGlobalFilter = useCallback(debounce(_setGlobalFilter, 1000), []);
  const [sorting, _setSorting] = useState<SortingState>([
    {
      id: "specimens__collected_date",
      desc: true,
    },
  ]);
  const setSorting: typeof _setSorting = (value) => {
    // @ts-ignore
    const newValue = value(sorting);
    if (!newValue?.length) {
      _setSorting([
        {
          id: "specimens__collected_date",
          desc: false,
        },
      ]);
    } else {
      _setSorting(value);
    }
  };

  const table = useReactTable({
    data: cases,
    columns,
    state: {
      globalFilter,
      sorting,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,

    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount,
    debugAll: false,
  });

  const orderingParam = sorting
    .map(
      (v) => `${v.desc ? "-" : ""}${v.desc ? v.id.replaceAll(",", ",-") : v.id}`
    )
    .join(",");
  const filterParams: Partial<Parameters<typeof listCases>[0]> = {};
  if (statusFilter) {
    filterParams.status = statusFilter;
  }
  if (clinicFilter) {
    filterParams.clinicId = clinicFilter;
  }
  if (collectedAtFilter) {
    filterParams.collectedAtStart =
      DateTime.fromISO(collectedAtFilter).startOf("day").toISO() || undefined;
    filterParams.collectedAtEnd =
      DateTime.fromISO(collectedAtFilter).endOf("day").toISO() || undefined;
  }
  if (globalFilter) {
    filterParams.search = globalFilter;
  }
  const filterParamsStr = Object.entries(filterParams)
    .map(([id, value]) => `${id}=${value}`)
    .join(",");

  // API Request
  useEffect(() => {
    dispatch(
      listCases({
        parentLookupLabStationId: station.id,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        ordering: orderingParam,
        ...filterParams,
      })
    ).then((response) => {
      setPageCount(Math.ceil(response.count / pageSize));
    });
  }, [station.id, pageSize, currentPage, orderingParam, filterParamsStr]);

  const sortOptions = table
    .getFlatHeaders()
    .filter((h) => h.column.getCanSort())
    .flatMap((h) => [
      { id: h.column.id, desc: true },
      { id: h.column.id, desc: false },
    ]);

  if (!casesReady) return <AppSpinner>Loading Cases...</AppSpinner>;

  return (
    <div className="Cases">
      <Helmet>
        <title>Cases</title>
      </Helmet>
      <header className="Cases__header">
        <figure className="Logo">
          <LogoSVG />
        </figure>

        <div>
          <p>
            <strong>{lab?.name}</strong>
          </p>
          <p>{station?.name}</p>
        </div>

        {false && (
          <select
            onChange={(e) =>
              setSorting([
                { id: "priority", desc: true },
                sortOptions[e.target.options.selectedIndex],
              ])
            }
          >
            {sortOptions.map((h) => (
              <option key={`${h.id},${h.desc}`} value={`${h.id} ${h.desc}`}>
                {h.id} {h.desc ? "desc" : "asc"}
              </option>
            ))}
          </select>
        )}
        <Link to="/station/accessioning/new">Start New Accession</Link>
      </header>
      <nav>
        <Link to="/cases">
          <strong>Cases</strong>
        </Link>
        <Link to="/users">Users</Link>
      </nav>

      <div className="Cases__search">
        <label className="Cases__search--statusFilter">
          <span>Status</span>
          <select
            onChange={(e) => setStatusFilter(e.target.value)}
            value={statusFilter}
          >
            <option value="">All Cases</option>
            {[
              Case.status.ORDERED,
              Case.status.WORKING,
              Case.status.ACTIVE,
              Case.status.FINALIZED,
            ].map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label className="Cases__search--filters">
          <span>Clinic</span>
          <select
            value={clinicFilter}
            onChange={(e) => setClinicFilter(parseInt(e.target.value))}
          >
            <option>All Clinics</option>
            {Object.values(clinics)
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </label>
        <label className="Cases__search--globalFilter">
          <span>Case Accession / Clinic Name / Patient Name</span>
          <input
            type="text"
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search"
          />
        </label>
        <label className="Cases__search--filters">
          <span>Collected Date</span>
          <input
            type="date"
            placeholder="Collected Date"
            onChange={(e) => setCollectedAtFilter(e.target.value)}
          />
        </label>
      </div>

      <div className="Cases__list">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: (
                            <>
                              <ChevronDownIcon />
                            </>
                          ),
                          desc: (
                            <>
                              <ChevronUpIcon />
                            </>
                          ),
                        }[header.column.getIsSorted() as string] ?? (
                          <svg height={0} width="1em"></svg>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`Cases__td Cases__td--${cell}`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="Cases__pagination">
          <div>
            <strong>
              {currentPage} of {pageCount}
            </strong>
          </div>
          <div className="Cases__pagination__buttons">
            <button
              className="border rounded p-1"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage <= 1}
            >
              {"<<"}
            </button>
            <button
              className="border rounded p-1"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {"<"}
            </button>
            <button
              className="border rounded p-1"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= pageCount}
            >
              {">"}
            </button>
            <button
              className="border rounded p-1"
              onClick={() => setCurrentPage(pageCount)}
              disabled={currentPage >= pageCount}
            >
              {">>"}
            </button>
          </div>

          <select
            value={pageSize}
            onChange={(e) => {
              setCurrentPage(1);
              setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((ps) => (
              <option key={ps} value={ps}>
                Show {ps}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default CaseAccession;
